import React, { lazy, useEffect } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import {
  FORGOT_PASSWORD,
  NOT_FOUND,
  RESET_PASSWORD,
  SIGN_IN
} from '../resources/links';
import PrivateRoute from './PrivateRoute';
import ErrorsLayout from '../components/layouts/ErrorsLayout';
import DashboardLayout from '../components/layouts/DashboardLayout';
import SignIn from '../views/auth/SignIn';
import NotFound from '../views/errors/NotFound';
import ForgotPassword from '../views/auth/ForgotPassword';
import ResetPassword from '../views/auth/ResetPassword';
import AuthLayout from '../components/layouts/AuthLayout';
import UserOnboarding from '../views/onboarding/UserOnboarding';
import OnboardingGuard from '../views/onboarding/OnboardingGuard';
import ReconciliationsOutlet from '../views/transactions/ReconciliationsOutlet';
import ReconciliationsList from '../views/transactions/ReconciliationsList';
import BranchesOutlet from '../views/branches/BranchesOutlet';
import LoanProductsOutlet from '../views/financing/loans/LoanProductsOutlet';
import PassbookSalesOutlet from '../views/customers/passbook-sales';
import PassbookSalesList from '../views/customers/passbook-sales/PassbookSalesList';
import RevenuesOutlet from '../views/revenues';
import { Company } from '../resources/types';
import FinancingOutlet from '../views/financing';
import HirePurchaseOutlet from '../views/financing/hire-purchase';
import WebsiteLayout from '../components/layouts/WebsiteLayout';

// Home
const DashboardView = lazy(() => import('../views/dashboard'));
const ReportsOutlet = lazy(() => import('../views/dashboard/reports/ReportsOutlet'));
const ReportsOverview = lazy(() => import('../views/dashboard/reports/ReportsOverview'));

const AccountsView = lazy(() => import('../views/accounts'));
const AccountsListView = lazy(() => import('../views/accounts/AccountsList'));
const AccountDetailsView = lazy(() => import('../views/accounts/AccountDetails'));
const CustomersView = lazy(() => import('../views/customers'));
const CustomersListView = lazy(() => import('../views/customers/CustomersList'));
const CustomerDetailsView = lazy(() => import('../views/customers/CustomerDetails'));
const AddCustomerView = lazy(() => import('../views/customers/add-customer/AddCustomer'));
const TransactionsView = lazy(() => import('../views/transactions'));
const LoanProductsListView = lazy(() => import('../views/financing/loans/LoanProductsList'));
const LoansListView = lazy(() => import('../views/financing/loans/LoansList'));
const HirePurchaseListView = lazy(() => import('../views/financing/hire-purchase/HirePurchaseList'));
const AddHirePurchaseView = lazy(() => import('../views/financing/hire-purchase/AddHirePurchase'));
const AddLoanView = lazy(() => import('../views/financing/loans/AddLoan'));
const LoanCalculatorView = lazy(() => import('../views/financing/loans/CalculateLoan'));
const LoanDetailsView = lazy(() => import('../views/financing/loans/LoanDetails'));
const LoansOutlet = lazy(() => import('../views/financing/loans'));
const FinancingDetailsView = lazy(() => import('../views/financing/FinancingDetails'));
const AccountStatementView = lazy(() => import('../views/accounts/ViewAccountTransactions'));

// 
const SettingsView = lazy(() => import('../views/settings'));
const ProfileDetailsView = lazy(() => import('../views/settings/ProfileDetails'));
const CompanyProfileView = lazy(() => import('../views/onboarding/CompanyProfile'));
const BillingDetailsView = lazy(() => import('../views/settings/billing/BillingDetails'));

const BranchesListView = lazy(() => import('../views/branches/BranchesList'));
const BranchDetailsView = lazy(() => import('../views/branches/BranchDetails'));
const CompaniesListView = lazy(() => import('../views/companies/CompaniesList'));
const CompaniesView = lazy(() => import('../views/companies'));
const CompanyDetailsView = lazy(() => import('../views/companies/CompanyDetails'));
const CompanyOnboardingView = lazy(() => import("../views/onboarding/CompanyOnboarding"));
const CreateAccountFlowView = lazy(() => import("../views/accounts/add-account/CreateAccountFlow"));
const UsersView = lazy(() => import('../views/users'));
const UsersListView = lazy(() => import('../views/users/UsersList'));
const UserDetailsView = lazy(() => import('../views/users/UserDetails'));
const RevenuesListView = lazy(() => import('../views/revenues/RevenuesList'));
const PaymentSuccessfulView = lazy(() => import("../views/payments/PaymentSuccess"))

// Products
const ProductsOutlet = lazy(() => import('../views/products'));
const ProductsListView = lazy(() => import('../views/products/ProductsList'));
const AddProductView = lazy(() => import('../views/products/AddProduct'));

// Website
const HomePageView = lazy(() => import("../views/website/home"));
const PrivacyPolicyView = lazy(() => import("../views/website/legal/PrivacyPolicy"));
const ToSView = lazy(() => import("../views/website/legal/TermsOfService"));
const ContactView = lazy(() => import("../views/website/Contact"));

const router = createBrowserRouter([
  {
    path: "/",
    element: <WebsiteLayout />,
    children: [
      {
        index: true,
        element: <HomePageView />
      },
      {
        path: "/privacy",
        element: <PrivacyPolicyView />
      },
      {
        path: "/terms",
        element: <ToSView />
      },
      {
        path: "/contact",
        element: <ContactView />
      },
    ]
  },
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      {
        path: FORGOT_PASSWORD,
        element: <ForgotPassword />,
        caseSensitive: true
      },
      {
        path: RESET_PASSWORD,
        element: <ResetPassword />,
        caseSensitive: true
      },
      {
        path: SIGN_IN,
        element: <SignIn />,
        caseSensitive: true,
        index: true
      }
    ]
  },
  {
    path: "/onboarding",
    children: [
      {
        path: "user",
        element: <PrivateRoute><UserOnboarding /></PrivateRoute>
      },
      {
        path: "company",
        element: <PrivateRoute><CompanyOnboardingView /></PrivateRoute>
      }
    ]
  },
  {
    path: "/payments/:payable/:payableId/success",
    element: <PaymentSuccessfulView />
  },
  {
    path: "/admin",
    element: <OnboardingGuard><DashboardLayout /></OnboardingGuard>,
    children: [
      {
        path: "dashboard",
        element: <DashboardView />,
        index: true,
        caseSensitive: true
      },
      {
        path: "reports",
        element: <ReportsOverview />,
        caseSensitive: true
      },
      {
        path: "accounts",
        element: <AccountsView />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <AccountsListView />
          },
          {
            path: "add",
            element: <CreateAccountFlowView isCreateAccount={true} />
          },
          {
            path: ":accountId",
            element: <AccountDetailsView />,
            caseSensitive: true,
            children: [
              {
                path: "statement",
                element: <AccountStatementView />,
                caseSensitive: true
              }
            ]
          }
        ]
      },
      {
        path: "customers",
        element: <CustomersView />,
        caseSensitive: true,
        children: [
          {
            element: <CustomersListView />,
            index: true,
            caseSensitive: true
          },
          {
            path: "add",
            element: <CreateAccountFlowView />,
            caseSensitive: true
          },
          {
            path: "add-account",
            element: <AddCustomerView />,
            caseSensitive: true
          },
          {
            path: ":id",
            element: <CustomerDetailsView />,
            caseSensitive: true,
            children: [

            ]
          },
          {
            path: ":id/add-account",
            element: <AddCustomerView />,
            caseSensitive: true
          }
        ]
      },
      {
        path: "companies",
        element: <CompaniesView />,
        caseSensitive: true,
        children: [
          {
            element: <CompaniesListView />,
            caseSensitive: true,
            index: true
          },
          {
            path: ":id",
            element: <CompanyDetailsView />,
            caseSensitive: true
          }
        ]
      },
      {
        path: "transactions",
        element: <TransactionsView />,
        caseSensitive: true
      },
      {
        path: "financing",
        element: <FinancingOutlet />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <LoansListView />
          },
          {
            element: <FinancingDetailsView />,
            path: ":id"
          },
          {
            element: <LoansOutlet />,
            path: "loans",
            children: [
              {
                index: true,
                element: <LoansListView />
              },
              {
                element: <AddLoanView />,
                path: 'add'
              },
              {
                element: <LoanCalculatorView />,
                path: 'calculate'
              },
            ]
          },
          {
            element: <HirePurchaseOutlet />,
            path: "hire-purchases",
            children: [
              {
                index: true,
                element: <HirePurchaseListView />,
              },
              {
                element: <AddHirePurchaseView />,
                path: "add"
              },
            ]
          },
          {
            element: <LoanProductsListView />,
            path: "loan-products"
          },
          {
            element: <LoanDetailsView />,
            path: ":id"
          }
        ]
      },
      {
        path: "loan-products",
        element: <LoanProductsOutlet />,
        caseSensitive: true,

      },
      {
        path: "agents",
        element: <UsersView />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <UsersListView />
          },
          {
            path: ":id",
            element: <UserDetailsView />
          }
        ]
      },
      {
        path: "products",
        element: <ProductsOutlet />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <ProductsListView />
          },
          {
            path: "add",
            element: <AddProductView />
          },
          {
            path: ":id/edit",
            element: <AddProductView isEdit={true} />
          },
          {
            path: ":id",
            element: <UserDetailsView />
          }
        ]
      },
      {
        path: "reconciliations",
        element: <ReconciliationsOutlet />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <ReconciliationsList />,
            caseSensitive: true
          }
        ]
      },
      {
        path: "passbooks",
        element: <PassbookSalesOutlet />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <PassbookSalesList />,
            caseSensitive: true
          }
        ]
      },
      {
        path: "branches",
        element: <BranchesOutlet />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <BranchesListView />,
            caseSensitive: true
          },
          {
            path: ":id",
            element: <BranchDetailsView />
          }
        ]
      },
      {
        path: "revenues",
        element: <RevenuesOutlet />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <RevenuesListView />,
            caseSensitive: true
          },
          {
            path: ":id",
            element: <RevenuesListView />
          }
        ]
      },
      {
        path: "settings",
        element: <SettingsView />,
        caseSensitive: true,
        children: [
          {
            index: true,
            element: <ProfileDetailsView />,
            caseSensitive: true
          },
          {
            path: "company",
            element: <CompanyProfileView onSuccess={function (company: Company): void {
              throw new Error('Function not implemented.');
            }} onFail={function (): void {
              throw new Error('Function not implemented.');
            }} formikRef={undefined} />,
            caseSensitive: true
          },
          {
            path: "branches",
            element: <BranchesListView />,
            caseSensitive: true
          },
          {
            path: "users",
            element: <UsersListView />,
            caseSensitive: true
          },
          {
            path: "billing",
            element: <BillingDetailsView />,
            caseSensitive: true
          }
        ]
      }
    ]
  },
  {
    element: <ErrorsLayout />,
    children: [
      {
        path: "/404",
        element: <NotFound />
      }
    ]
  },
  {
    path: "*",
    element: <Navigate to={NOT_FOUND} replace />
  },
])

export default router;
